import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { TABLES } from '../lib/constants';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiFacebook, FiLink, FiArrowLeft, FiPackage, FiChevronLeft, FiChevronRight, FiCheckCircle, FiBox, FiClock, FiShare2, FiHeart } = FiIcons;

const ITEMS_PER_PAGE = 12;

const PublicFeeds = () => {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [copyingId, setCopyingId] = useState(null);
  const [likedItems, setLikedItems] = useState(new Set());

  useEffect(() => {
    fetchFeeds();
    // Load local likes
    const localLikes = JSON.parse(localStorage.getItem('rox_liked_ids') || '[]');
    setLikedItems(new Set(localLikes));
  }, [page]);

  const fetchFeeds = async () => {
    setLoading(true);
    try {
      const { count } = await supabase.from(TABLES.LOGS).select('*', { count: 'exact', head: true });
      setTotalCount(count || 0);

      const { data, error } = await supabase
        .from(TABLES.LOGS)
        .select(`
          id, 
          created_at, 
          proof_url, 
          quantity,
          likes,
          pilot:pilot_id(username, nickname),
          item:item_id(name, category)
        `)
        .order('created_at', { ascending: false })
        .range(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE - 1);

      if (error) throw error;
      setFeeds(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (id, currentLikes) => {
    if (likedItems.has(id)) return;

    // Optimistic UI update
    setFeeds(prev => prev.map(f => f.id === id ? { ...f, likes: (f.likes || 0) + 1 } : f));
    
    const newLikedItems = new Set([...likedItems, id]);
    setLikedItems(newLikedItems);
    localStorage.setItem('rox_liked_ids', JSON.stringify([...newLikedItems]));

    try {
      const { error } = await supabase
        .from(TABLES.LOGS)
        .update({ likes: (currentLikes || 0) + 1 })
        .eq('id', id);
      
      if (error) throw error;
    } catch (err) {
      // Rollback on error
      console.error(err);
      setFeeds(prev => prev.map(f => f.id === id ? { ...f, likes: currentLikes } : f));
      newLikedItems.delete(id);
      setLikedItems(new Set(newLikedItems));
      localStorage.setItem('rox_liked_ids', JSON.stringify([...newLikedItems]));
    }
  };

  const handleCopyLink = (id) => {
    const url = `${window.location.origin}/#/feeds?drop=${id}`;
    navigator.clipboard.writeText(url);
    setCopyingId(id);
    setTimeout(() => setCopyingId(null), 2000);
  };

  const shareToFacebook = (id) => {
    const url = `${window.location.origin}/#/feeds?drop=${id}`;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className="min-h-screen bg-[#f5f5f7] pb-24">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="#/login" className="p-2.5 bg-gray-100 rounded-2xl text-gray-500 hover:text-gray-900 transition-all active:scale-95">
              <SafeIcon icon={FiArrowLeft} size={20} />
            </a>
            <div>
              <h1 className="text-xl font-black text-gray-900 tracking-tight">Activity Feed</h1>
              <div className="flex items-center gap-1.5 text-[10px] font-black text-blue-500 uppercase tracking-widest">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" /> Live Stream
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-3 px-5 py-2.5 bg-black text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl">
            <SafeIcon icon={FiBox} className="text-blue-400" /> {totalCount} Recorded
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 pt-12">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-40 gap-4">
            <div className="w-12 h-12 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin" />
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Fetching Logs...</p>
          </div>
        ) : feeds.length === 0 ? (
          <div className="text-center py-40 bg-white rounded-[3rem] border border-dashed border-gray-200">
            <SafeIcon icon={FiPackage} size={64} className="mx-auto text-gray-200 mb-6" />
            <h2 className="text-2xl font-black text-gray-400 uppercase tracking-widest">No Activity</h2>
          </div>
        ) : (
          <div className="space-y-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {feeds.map((feed) => (
                <motion.div key={feed.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="group">
                  <div className="bento-card !p-0 overflow-hidden border-2 border-transparent hover:border-blue-100 transition-all hover:shadow-2xl h-full flex flex-col">
                    <div className="relative aspect-video bg-gray-50 overflow-hidden shrink-0">
                      {feed.proof_url ? (
                        <img src={feed.proof_url} alt={feed.item?.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 text-gray-300">
                          <SafeIcon icon={FiPackage} size={32} />
                        </div>
                      )}
                      
                      <div className="absolute top-3 left-3 flex gap-1.5">
                        <span className="px-2.5 py-1 bg-black/50 backdrop-blur-md text-white text-[8px] font-black uppercase rounded-full border border-white/10">
                          {feed.item?.category || 'General'}
                        </span>
                      </div>

                      <div className="absolute top-3 right-3 flex flex-col gap-2 translate-x-12 group-hover:translate-x-0 transition-transform duration-500">
                        <button onClick={() => shareToFacebook(feed.id)} className="p-2.5 bg-white/90 backdrop-blur rounded-xl text-blue-600 shadow-xl hover:bg-white transition-all">
                          <SafeIcon icon={FiFacebook} size={16} />
                        </button>
                        <button onClick={() => handleCopyLink(feed.id)} className="p-2.5 bg-white/90 backdrop-blur rounded-xl text-gray-600 shadow-xl hover:bg-white transition-all">
                          <SafeIcon icon={copyingId === feed.id ? FiCheckCircle : FiLink} size={16} />
                        </button>
                      </div>

                      {/* Floating Like Button for Mobile/Hover */}
                      <button 
                        onClick={() => handleLike(feed.id, feed.likes)}
                        className={`absolute bottom-3 right-3 px-3 py-2 rounded-xl backdrop-blur-md flex items-center gap-2 transition-all shadow-xl ${likedItems.has(feed.id) ? 'bg-red-500 text-white' : 'bg-white/90 text-gray-900 hover:bg-red-50 hover:text-red-500'}`}
                      >
                        <FiHeart className={likedItems.has(feed.id) ? 'fill-current' : ''} />
                        <span className="text-[10px] font-black">{feed.likes || 0}</span>
                      </button>
                    </div>

                    <div className="p-5 flex-1 flex flex-col">
                      <h3 className="text-lg font-black text-gray-900 leading-tight mb-3">
                        {feed.item?.name} {feed.quantity > 1 && <span className="text-gray-300">x{feed.quantity}</span>}
                      </h3>
                      
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-5 h-5 bg-blue-600 rounded-md flex items-center justify-center text-[8px] font-black text-white">
                          {(feed.pilot?.nickname || feed.pilot?.username || '?').charAt(0).toUpperCase()}
                        </div>
                        <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest truncate">
                          {feed.pilot?.nickname || feed.pilot?.username}
                        </p>
                      </div>

                      <div className="mt-auto pt-4 border-t border-gray-50 flex items-center justify-between text-[9px] font-black text-gray-300 uppercase tracking-tighter">
                        <div className="flex items-center gap-1.5">
                          <SafeIcon icon={FiClock} /> {new Date(feed.created_at).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-3">
                           <button onClick={() => shareToFacebook(feed.id)} className="text-gray-400 hover:text-blue-600 transition-colors">
                            <SafeIcon icon={FiShare2} size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col items-center gap-4 mt-12">
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="p-3 bg-white rounded-xl border border-gray-100 disabled:opacity-30">
                    <SafeIcon icon={FiChevronLeft} />
                  </button>
                  <span className="text-[10px] font-black uppercase tracking-widest px-4">Page {page + 1} of {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1} className="p-3 bg-white rounded-xl border border-gray-100 disabled:opacity-30">
                    <SafeIcon icon={FiChevronRight} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default PublicFeeds;